import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Accordion, AccordionGroup, Button, Loader, Sidebar, Slider } from 'ui';
import { formatDate, formatPrice, formatPriceCurrency } from 'utils';



import { useTranslation } from '@/i18n/client';
import { addToCartEvent, viewProductPageEvent } from '@/modules/google/data-layer/data-layer';
import { findAttributionToken } from '@/modules/google/retail/utilites/attribution-tokens';
import { fetchProduct, Product } from '@/modules/product/api/singleProduct';
import { Image } from '@/shared/components/image';
import { useBasket } from '@/shared/context/basket-context';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';
import { productName } from '@/shared/utils/product-name';
import { renderShippingTime } from '@/shared/utils/shipping-time';



import { ProductAttributes } from '../productAttributes';
import styles from './ProductSidebar.module.scss';
import { ProductSidebarProps } from './types';
import { imageBreakpoints } from './utils';





export const ProductSidebar = ({
  ancillaryProductSlug,
  shippingMethods,
  withAddToCartButton,
  isOpen = false,
  onAddToCart,
  onClose,
}: ProductSidebarProps) => {
  const [ancillaryProduct, setAncillaryProduct] = useState<Product | null>(
    null
  );
  const [shipmentTime, setShipmentTime] = useState<string | null>(null);
  const { addToBasket, setIsCartSidebarOpen, freeShippingPrice } = useBasket();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(['products']);
  const locale = useCurrentLocale();

  const closeSidebar = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!ancillaryProductSlug) {
      setAncillaryProduct(null);
      return;
    }
    setIsLoading(true);

    fetchProduct(ancillaryProductSlug as string).then((res) => {
      if (!res) {
        closeSidebar();
        setAncillaryProduct(null);
        return;
      }

      setAncillaryProduct(res);
      setIsLoading(false);

      viewProductPageEvent(res.currency, [
        {
          productId: res.productId,
          shippingGroup: 'A',
          quantity: 1,
          productName: productName(res),
          productSlug: res.slug,
          photo: res.images[0],
          shippingTime: 0,
          price: {
            amount: res.price,
            currency: res.currency,
          },
          categoryPath: res.categoryHomePath.map((category) => category.name),
          brandName: res.brandName,
          product: res,
        },
      ]);
    });
  }, [ancillaryProductSlug, closeSidebar]);

  useEffect(() => {
    if (!ancillaryProduct || !shippingMethods) return;

    const { shipmentTime } = renderShippingTime(
      ancillaryProduct.shippingTime,
      locale,
      ancillaryProduct.shippingDeliverySettings,
      ancillaryProduct.defaultCalendar
    );

    setShipmentTime(shipmentTime);
  }, [ancillaryProduct, locale, shippingMethods]);

  function handleAddToCart() {
    if (!ancillaryProduct) return;

    if (onAddToCart) {
      onAddToCart(ancillaryProduct.slug);
    }

    const amountProductAddedToCart = 1;

    closeSidebar();
    setIsCartSidebarOpen(true);
    addToBasket(ancillaryProduct.productId, amountProductAddedToCart);
    const attributionToken = findAttributionToken(ancillaryProduct.productId);
    addToCartEvent([
      {
        name: productName(ancillaryProduct),
        brand: ancillaryProduct.brandName,
        categoryPath: ancillaryProduct.categoryHomePath,
        currency: ancillaryProduct.currency,
        id: ancillaryProduct.productId,
        price: ancillaryProduct.price,
        quantity: amountProductAddedToCart,
        importFlag: ancillaryProduct.importFlag,
        attributionToken: attributionToken,
      },
    ]);
  }

  return (
    <Sidebar isOpen={isOpen} onOverlayPress={closeSidebar}>
      <div className={styles.productSidebar}>
        <Slider
          allowSwipe={true}
          hasPagination
          hasArrows={
            ancillaryProduct ? ancillaryProduct.images.length > 1 : false
          }
        >
          {ancillaryProduct?.images.map((image) => {
            return (
              <div
                className={styles.productSidebar__imageSlider__slide}
                key={image}
              >
                <Image
                  alt={productName(ancillaryProduct)}
                  title={productName(ancillaryProduct)}
                  className={
                    styles['productSidebar__imageSlider__slide--image']
                  }
                  src={image}
                  fetchPriority="low"
                  breakpoints={imageBreakpoints}
                />
              </div>
            );
          })}
        </Slider>
        <h2 className={styles.productSidebar__productName}>
          {productName(ancillaryProduct)}
        </h2>
        <div className={styles.productSidebar__productNumbers}>
          <Link
            prefetch={false}
            href={`${routes[locale].SINGLE_BRAND}/${ancillaryProduct?.brandSlug}`}
            className={styles['productSidebar__productNumbers--number']}
          >
            {ancillaryProduct?.brandName}
          </Link>
          {ancillaryProduct?.series &&
          !['N/A'].includes(ancillaryProduct?.series) ? (
            <>
              <hr
                className={styles['productSidebar__productNumbers--divider']}
              />
              <Link
                prefetch={false}
                href={`${routes[locale].SINGLE_BRAND}/${ancillaryProduct?.brandSlug}?filters[series][]=${ancillaryProduct?.series}`}
                className={styles['productSidebar__productNumbers--number']}
              >
                {ancillaryProduct?.series}
              </Link>
            </>
          ) : null}
        </div>
        <div className={styles.productSidebar__priceWrapper}>
          <span className={styles['productSidebar__priceWrapper--price']}>
            {
              formatPriceCurrency(
                ancillaryProduct?.price ?? 0,
                ancillaryProduct?.locale,
                ancillaryProduct?.currency
              ).price
            }
          </span>
          <span className={styles['productSidebar__priceWrapper--currency']}>
            &nbsp;
            {
              formatPriceCurrency(
                ancillaryProduct?.price ?? 0,
                ancillaryProduct?.locale,
                ancillaryProduct?.currency
              ).currency
            }
          </span>
          {ancillaryProduct?.previousPrice &&
          ancillaryProduct?.previousPrice > 0 ? (
            <span
              className={
                styles['productSidebar__priceWrapper--discountedPrice']
              }
            >
              {formatPrice(
                ancillaryProduct?.previousPrice ?? 0,
                ancillaryProduct?.locale,
                ancillaryProduct?.currency
              )}
            </span>
          ) : null}
        </div>

        {ancillaryProduct ? (
          <div className={styles['stockAndShipmentTime']}>
            {shipmentTime ? (
              <div className={styles['stockAndShipmentTime__item']}>
                <div className={styles['stockAndShipmentTime__label']}>
                  {t('common.shipment')}
                </div>
                <div className={styles['stockAndShipmentTime__value']}>
                  {t(shipmentTime)}
                </div>
              </div>
            ) : null}

            <div className={styles['stockAndShipmentTime__item']}>
              <div className={styles['stockAndShipmentTime__label']}>
                {t('common.inStock')}
              </div>
              <div className={styles['stockAndShipmentTime__value']}>
                {ancillaryProduct.stock} {ancillaryProduct.packageType}
              </div>
            </div>
          </div>
        ) : null}

        {withAddToCartButton ? (
          <div className={styles['addToCart']}>
            <Button
              variant="leaf"
              onPress={handleAddToCart}
              isDisabled={ancillaryProduct?.stock === 0}
            >
              {t(
                ancillaryProduct?.stock !== 0
                  ? 'common:common.addToCart'
                  : 'common:common.outOfStock'
              )}
            </Button>
          </div>
        ) : null}

        {ancillaryProduct?.promotionDateIsShowed && (
          <p className={styles.productSidebar__discountInformation}>
            <Trans
              i18nKey="singleProduct.saving"
              t={t}
              components={[<b key={0} />]}
              values={{
                price: formatPrice(
                  ancillaryProduct.price -
                    (ancillaryProduct?.previousPrice || 0),
                  ancillaryProduct.locale,
                  ancillaryProduct.currency
                ),
              }}
            />
            {ancillaryProduct?.promotionDateIsShowed &&
            ancillaryProduct.promotionEndDate
              ? ` ${t('singleProduct.discountValidDate')} ${formatDate(
                  ancillaryProduct.promotionEndDate
                )}`
              : null}
          </p>
        )}
        <div className={styles.productSidebar__accordions}>
          <AccordionGroup>
            <Accordion
              containerClassNames={styles.productSidebar__accordion}
              heading={t('singleProduct.specification')}
              expanded={true}
            >
              <div
                className={
                  styles.productSidebar__accordion__list__specification
                }
              >
                {ancillaryProduct && (
                  <ProductAttributes product={ancillaryProduct} />
                )}
              </div>
            </Accordion>
          </AccordionGroup>
        </div>
      </div>
      {isLoading && <Loader backgroundColor="#fff" />}
    </Sidebar>
  );
};
